import React from 'react';
import { useNavigate } from 'react-router-dom';
import HourInput from './HourInput';
import MemoPopover from './MemoPopover';
import { FONT, GRID_COLS, SUB_PANEL_INDENT } from '../constants';
import { C, displayBox, inputBox } from '../styles';
import { fmtMoney, rowAmounts } from '../helpers';
import type { Row, PayMethod } from '../types';

interface Props {
  row: Row;
  isLast: boolean;
  isExpanded: boolean;
  isSelected: boolean;
  openMenuId: string | number | null;
  openPayMethodId: string | number | null;
  onSelect: (id: string | number | null) => void;
  onToggleExpanded: (id: string | number) => void;
  onToggleIncluded: (id: string | number) => void;
  onUpdateField: <K extends keyof Row>(id: string | number, field: K, value: Row[K]) => void;
  onSaveMemo: (id: string | number, text: string, applyAll: boolean) => void;
  onSkip: (id: string | number) => void;
  onAddBack: (id: string | number) => void;
  onOpenEditDrawer: (id: string | number) => void;
  onSetOpenMenuId: (id: string | number | null) => void;
  onSetOpenPayMethodId: (id: string | number | null) => void;
}

export default function EmployeeRow({
  row: r, isLast, isExpanded, isSelected,
  openMenuId, openPayMethodId,
  onSelect, onToggleExpanded, onToggleIncluded, onUpdateField, onSaveMemo,
  onSkip, onAddBack, onOpenEditDrawer,
  onSetOpenMenuId, onSetOpenPayMethodId,
}: Props) {
  const navigate = useNavigate();
  const a = rowAmounts(r);
  const rowBg = r.skipped ? '#FAFBFC' : (isSelected ? C.brandBg : 'transparent');

  return (
    <>
      <div
        id={'row-' + r.id}
        onClick={() => onSelect(isSelected ? null : r.id)}
        style={{
          padding: '16px 20px',
          borderBottom: (isLast && !isExpanded) || isExpanded ? 'none' : '1px solid ' + C.line,
          display: 'grid', gridTemplateColumns: GRID_COLS, gap: 16,
          alignItems: 'center', opacity: r.ready ? 1 : 0.5,
          position: 'relative', background: rowBg,
          borderLeft: isSelected ? '3px solid ' + C.brand : '3px solid transparent',
          cursor: 'pointer',
        }}
      >
        <div>
          <input
            type="checkbox"
            checked={r.included}
            disabled={!r.ready || r.skipped}
            onChange={() => onToggleIncluded(r.id)}
            onClick={(e) => e.stopPropagation()}
            style={{ width: 16, height: 16, accentColor: C.brand, cursor: r.ready ? 'pointer' : 'not-allowed' }}
          />
        </div>
        <div>
          <button
            type="button"
            title={isExpanded ? 'Hide additional hours' : 'Add overtime, vacation, or sick hours'}
            aria-expanded={isExpanded}
            onClick={(e) => { e.stopPropagation(); onToggleExpanded(r.id); }}
            style={{
              background: 'transparent', border: 'none', padding: 0,
              width: 24, height: 24,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', position: 'relative',
              color: isExpanded ? C.brandDark : C.muted,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s ease' }}>
              <polyline points="9 18 15 12 9 6" />
            </svg>
            {!isExpanded && a.extraHours > 0 && (
              <span style={{ position: 'absolute', top: 1, right: 0, width: 6, height: 6, borderRadius: '50%', background: C.brand }} />
            )}
          </button>
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              onClick={(e) => { e.stopPropagation(); onOpenEditDrawer(r.id); }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.textDecoration = 'underline'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.textDecoration = 'none'; }}
              style={{
                fontSize: 14, fontWeight: 600,
                color: r.skipped ? '#4B5563' : C.ink,
                cursor: 'pointer', textDecoration: 'none', display: 'inline-block',
              }}
            >
              {r.name}
            </div>
            {r.skipped && (
              <span style={{
                padding: '2px 8px', background: '#E5E7EB', color: '#4B5563',
                borderRadius: 4, fontSize: 10.5, fontWeight: 700,
                letterSpacing: '0.04em', textTransform: 'uppercase',
              }}>
                Skipped
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: C.muted }}>
            ${r.hourlyRate.toFixed(2)}/hr {r.position ? '· ' + r.position : ''}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <HourInput value={r.regular} onChange={(v) => onUpdateField(r.id, 'regular', v)} disabled={!r.ready || r.skipped} style={{ ...inputBox, width: 90 }} />
        </div>
        <div style={{ textAlign: 'right' }}>
          <HourInput value={r.statHoliday} onChange={(v) => onUpdateField(r.id, 'statHoliday', v)} disabled={!r.ready || r.skipped} style={{ ...inputBox, width: 90 }} />
        </div>
        <div style={{ textAlign: 'right', position: 'relative' }}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <span style={{
              position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
              fontSize: 13, color: r.statAvgDaily === '' || r.statAvgDaily == null ? C.faint : C.ink,
              pointerEvents: 'none', fontFamily: FONT,
            }}>$</span>
            <input
              type="text"
              inputMode="decimal"
              value={r.statAvgDaily === '' || r.statAvgDaily == null ? '' : String(r.statAvgDaily)}
              onChange={(e) => {
                const v = e.target.value;
                onUpdateField(r.id, 'statAvgDaily', v === '' ? '' : (parseFloat(v) || 0));
              }}
              onClick={(e) => e.stopPropagation()}
              disabled={!r.ready || r.skipped}
              placeholder="0.00"
              style={{ ...inputBox, width: 90, paddingLeft: 20 }}
            />
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ ...displayBox, width: 90 }}>
            {a.totalHours > 0 ? (a.totalHours % 1 === 0 ? String(a.totalHours) : a.totalHours.toFixed(2)) + 'h' : '0h'}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ ...displayBox, width: 90 }}>{fmtMoney(a.gross)}</div>
        </div>
        <div style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
          <MemoPopover value={r.memo} onSave={(text, applyAll) => onSaveMemo(r.id, text, applyAll)} />
        </div>
        <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
          <span
            onClick={() => onSetOpenPayMethodId(openPayMethodId === r.id ? null : r.id)}
            style={{
              background: r.payMethod === 'Cheque' ? C.amberBg : C.brandBg,
              color: r.payMethod === 'Cheque' ? C.amberText : C.brandDarkText,
              fontSize: 12, padding: '5px 12px', borderRadius: 6,
              fontWeight: 600, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            {r.payMethod} ▾
          </span>
          {openPayMethodId === r.id && (
            <div style={{
              position: 'absolute', top: 28, left: 0,
              background: '#fff', border: '1px solid ' + C.line,
              borderRadius: 6, boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
              width: 150, zIndex: 10, overflow: 'hidden',
            }}>
              {(['Direct deposit', 'Cheque'] as PayMethod[]).map((method, i) => (
                <div
                  key={method}
                  onClick={() => {
                    onUpdateField(r.id, 'payMethod', method);
                    onSetOpenPayMethodId(null);
                  }}
                  style={{
                    padding: '8px 12px', fontSize: 13, color: C.ink,
                    cursor: 'pointer',
                    borderTop: i === 0 ? 'none' : '1px solid ' + C.line,
                  }}
                >
                  {method}
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ textAlign: 'center', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => onSetOpenMenuId(openMenuId === r.id ? null : r.id)}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              fontSize: 18, color: C.muted, padding: 4,
            }}
          >
            ⋮
          </button>
          {openMenuId === r.id && (
            <div style={{
              position: 'absolute', top: 30, right: 0,
              background: '#fff', border: '1px solid ' + C.line,
              borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
              width: 200, overflow: 'hidden', zIndex: 20, textAlign: 'left',
            }}>
              <div
                onClick={() => { onOpenEditDrawer(r.id); onSetOpenMenuId(null); }}
                style={{ padding: '10px 14px', fontSize: 13, color: C.ink, cursor: 'pointer' }}
              >
                Edit paycheque
              </div>
              <div
                onClick={() => navigate('/payroll/employees/' + r.id)}
                style={{ padding: '10px 14px', fontSize: 13, color: C.ink, cursor: 'pointer', borderTop: '1px solid ' + C.line }}
              >
                View profile
              </div>
              {r.skipped ? (
                <div
                  onClick={() => { onAddBack(r.id); onSetOpenMenuId(null); }}
                  style={{
                    padding: '10px 14px', fontSize: 13,
                    color: C.brand, cursor: 'pointer',
                    borderTop: '1px solid ' + C.line, fontWeight: 600,
                  }}
                >
                  Add back to payroll
                </div>
              ) : (
                <div
                  onClick={() => { onSkip(r.id); onSetOpenMenuId(null); }}
                  style={{
                    padding: '10px 14px', fontSize: 13,
                    color: C.danger, cursor: 'pointer',
                    borderTop: '1px solid ' + C.line,
                  }}
                >
                  Skip from payroll run
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {isExpanded && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            padding: '4px 20px 18px ' + SUB_PANEL_INDENT + 'px',
            borderBottom: isLast ? 'none' : '1px solid ' + C.line,
            background: isSelected ? C.brandBg : C.page,
            borderLeft: isSelected ? '3px solid ' + C.brand : '3px solid transparent',
            opacity: r.ready ? 1 : 0.5,
          }}
        >
          <div style={{
            border: '1px solid ' + C.line, borderRadius: 10,
            background: '#fff', padding: '14px 18px',
            display: 'inline-block',
          }}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: C.muted,
              textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 12,
            }}>
              Additional hours
            </div>
            <div style={{ display: 'flex', gap: 26, alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.ink, marginBottom: 6 }}>
                  Overtime <span style={{ color: C.faint, fontWeight: 500 }}>(1.5x)</span>
                </div>
                <HourInput value={r.overtime} onChange={(v) => onUpdateField(r.id, 'overtime', v)} disabled={!r.ready || r.skipped} style={{ ...inputBox, width: 100 }} />
                <div style={{ fontSize: 12, color: C.faint, marginTop: 6, textAlign: 'right', width: 100, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(a.otPay)}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.ink, marginBottom: 6 }}>Vacation</div>
                <HourInput value={r.vacation} onChange={(v) => onUpdateField(r.id, 'vacation', v)} disabled={!r.ready || r.skipped} style={{ ...inputBox, width: 100 }} />
                <div style={{ fontSize: 12, color: C.faint, marginTop: 6, textAlign: 'right', width: 100, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(a.vacPay)}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.ink, marginBottom: 6 }}>Sick</div>
                <HourInput value={r.sick} onChange={(v) => onUpdateField(r.id, 'sick', v)} disabled={!r.ready || r.skipped} style={{ ...inputBox, width: 100 }} />
                <div style={{ fontSize: 12, color: C.faint, marginTop: 6, textAlign: 'right', width: 100, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(a.sickPay)}</div>
              </div>
              <div style={{
                borderLeft: '1px solid ' + C.line,
                paddingLeft: 26, alignSelf: 'stretch',
                display: 'flex', flexDirection: 'column', justifyContent: 'center',
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.ink, marginBottom: 4 }}>Additional pay</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.ink, fontVariantNumeric: 'tabular-nums' }}>
                  {fmtMoney(a.otPay + a.vacPay + a.sickPay)}
                </div>
                <div style={{ fontSize: 12, color: C.faint, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>
                  {a.extraHours % 1 === 0 ? String(a.extraHours) : a.extraHours.toFixed(2)}h at ${r.hourlyRate.toFixed(2)}/hr base
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}